import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MessageSquareText, CheckCircle2, Loader2 } from 'lucide-react';

import Button from '../../../components/ui/Button';
import TextInput from '../../../components/ui/TextInput.jsx';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Intro = styled.div`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 12px;
  padding: 16px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const QCard = styled.div`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const QText = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};

  span { color: ${({ theme }) => theme.colors.auxiliar.danger}; margin-left: 3px; }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ScaleChip = styled.button`
  min-width: 40px;
  height: 40px;
  padding: 0 10px;
  border-radius: 8px;
  cursor: pointer;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 14px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary.main : '#E8ECF0')};
  background: ${({ theme, $active }) => ($active ? theme.colors.primary.main : '#fff')};
  color: ${({ theme, $active }) => ($active ? '#fff' : theme.colors.text.dark.little)};
  transition: all 0.12s;
`;

const ScaleHint = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const ChoiceWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Choice = styled.button`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 13px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  padding: 8px 14px;
  border-radius: 999px;
  cursor: pointer;
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary.main : '#E8ECF0')};
  background: ${({ theme, $active }) => ($active ? theme.colors.primary.main : '#fff')};
  color: ${({ theme, $active }) => ($active ? '#fff' : theme.colors.text.dark.little)};
  transition: all 0.12s;
`;

const ErrorText = styled.div`
  color: ${({ theme }) => theme.colors.auxiliar.danger};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 56px 24px;
  text-align: center;
`;

const IconCircle = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`;

const BigText = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SmallText = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  max-width: 420px;
`;

const DEFAULT_QUESTIONS = [
  { key: 'overall_satisfaction', type: 'likert_5', category: 'overall', isRequired: true },
  { key: 'manager_relationship', type: 'likert_5', category: 'manager', isRequired: false },
  { key: 'role_growth', type: 'likert_5', category: 'growth', isRequired: false },
  { key: 'recommend_score', type: 'nps', category: 'nps', isRequired: true },
  { key: 'main_reason', type: 'free_text', category: 'reason', isRequired: true },
  { key: 'what_to_improve', type: 'free_text', category: 'improvement', isRequired: false },
];

const REASON_CATEGORIES = [
  'compensation', 'career_growth', 'manager_relationship',
  'work_life_balance', 'company_culture', 'role_mismatch',
  'health_personal', 'other',
];

export default function ExitInterviewTab({ interview, onSubmit }) {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState({});
  const [reasonCategory, setReasonCategory] = useState(null);
  const [reasonDetail, setReasonDetail] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const completed = interview?.status === 'completed';

  const npsQuestion = useMemo(() => DEFAULT_QUESTIONS.find((q) => q.type === 'nps'), []);

  if (!interview) {
    return (
      <Centered>
        <IconCircle $bg="#F5F7FA" $color="#667">
          <MessageSquareText size={26} />
        </IconCircle>
        <BigText>{t('myOffboarding.interview.emptyTitle')}</BigText>
        <SmallText>{t('myOffboarding.interview.emptyText')}</SmallText>
      </Centered>
    );
  }

  if (completed) {
    return (
      <Centered>
        <IconCircle $bg="rgba(2,211,191,0.12)" $color="#01A899">
          <CheckCircle2 size={28} />
        </IconCircle>
        <BigText>{t('myOffboarding.interview.completedTitle')}</BigText>
        <SmallText>{t('myOffboarding.interview.completedText')}</SmallText>
      </Centered>
    );
  }

  const setAnswer = (key, value) => setAnswers((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    const missing = DEFAULT_QUESTIONS.find((q) => q.isRequired
      && (answers[q.key] === undefined || answers[q.key] === null || answers[q.key] === ''));
    if (missing) {
      setError(t('myOffboarding.interview.missingRequired'));
      return;
    }
    setError('');

    const answersJson = JSON.stringify(
      DEFAULT_QUESTIONS.map((q) => ({
        key: q.key,
        type: q.type,
        category: q.category,
        value: answers[q.key] ?? null,
      })),
    );
    const npsValue = npsQuestion ? answers[npsQuestion.key] : null;

    setBusy(true);
    await onSubmit(interview.id, {
      answersJson,
      npsScore: npsValue != null ? Number(npsValue) : null,
      reasonCategory,
      reasonDetail: reasonDetail || null,
    });
    setBusy(false);
  };

  return (
    <Wrap>
      <Intro>{t('myOffboarding.interview.intro')}</Intro>

      {DEFAULT_QUESTIONS.map((q) => (
        <QCard key={q.key}>
          <QText>
            {t(`myOffboarding.interview.question.${q.key}`)}
            {q.isRequired && <span>*</span>}
          </QText>

          {q.type === 'likert_5' && (
            <ChipRow>
              {[1, 2, 3, 4, 5].map((v) => (
                <ScaleChip key={v} $active={answers[q.key] === v} onClick={() => setAnswer(q.key, v)}>
                  {v}
                </ScaleChip>
              ))}
            </ChipRow>
          )}

          {q.type === 'nps' && (
            <>
              <ChipRow>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((v) => (
                  <ScaleChip key={v} $active={answers[q.key] === v} onClick={() => setAnswer(q.key, v)}>
                    {v}
                  </ScaleChip>
                ))}
              </ChipRow>
              <ScaleHint>
                <span>{t('myOffboarding.interview.npsLow')}</span>
                <span>{t('myOffboarding.interview.npsHigh')}</span>
              </ScaleHint>
            </>
          )}

          {q.type === 'free_text' && (
            <TextInput
              type="textarea"
              rows={3}
              value={answers[q.key] || ''}
              onChange={(e) => setAnswer(q.key, e.target.value)}
            />
          )}
        </QCard>
      ))}

      <QCard>
        <QText>{t('myOffboarding.interview.reasonCategory')}</QText>
        <ChoiceWrap>
          {REASON_CATEGORIES.map((c) => (
            <Choice key={c} $active={reasonCategory === c} onClick={() => setReasonCategory(c)}>
              {t(`myOffboarding.interview.reasonCategoryItems.${c}`)}
            </Choice>
          ))}
        </ChoiceWrap>
      </QCard>

      <QCard>
        <QText>{t('myOffboarding.interview.reasonDetail')}</QText>
        <TextInput
          type="textarea"
          rows={3}
          value={reasonDetail}
          onChange={(e) => setReasonDetail(e.target.value)}
        />
      </QCard>

      {error && <ErrorText>{error}</ErrorText>}

      <div>
        <Button variant="primary" disabled={busy} onClick={handleSubmit}>
          {busy ? <Loader2 size={16} /> : t('myOffboarding.interview.action.submit')}
        </Button>
      </div>
    </Wrap>
  );
}
